/**
 * Contrôle de cohérence de la date d'entretien du CSI, côté navigateur.
 *
 * Même règle que csi-backend/src/doctorant/date-entretien.ts, qui fait foi et
 * bloque de toute façon côté serveur. Ici, on l'applique au moment de la
 * saisie pour afficher un message clair, dans la langue du formulaire :
 *  - pas avant la date de première inscription du doctorant ;
 *  - pas dans le futur ;
 *  - pas plus de 12 mois en arrière (fenêtre glissante).
 *
 * La borne « première inscription » n'est appliquée que si cette date est
 * fiable : beaucoup de dossiers l'ont à null ou fausse (reliquats d'import,
 * tous antérieurs à 2015).
 */
export type MotifDateEntretien = 'invalide' | 'hors_periode' | 'trop_ancienne';

/** En deçà de cette année, la date de première inscription est ignorée. */
const ANNEE_MIN_INSCRIPTION_FIABLE = 2015;

export const MESSAGES_DATE_ENTRETIEN_FR: Record<MotifDateEntretien, string> = {
    invalide: "La date d'entretien est invalide.",
    hors_periode:
        "La date d'entretien semble incohérente. Vérifiez qu'elle est bien comprise entre la date de première inscription et aujourd'hui.",
    trop_ancienne: "La date d'entretien remonte à plus de 12 mois. Vérifiez l'année.",
};

export const MESSAGES_DATE_ENTRETIEN_EN: Record<MotifDateEntretien, string> = {
    invalide: 'The interview date is invalid.',
    hors_periode:
        "The interview date looks inconsistent. Please check that it falls between the student's first registration date and today.",
    trop_ancienne: 'The interview date is more than 12 months in the past. Please check the year.',
};

const UN_JOUR_MS = 24 * 60 * 60 * 1000;

function enDate(valeur: unknown): Date | null {
    if (valeur === null || valeur === undefined || valeur === '') return null;
    const d = valeur instanceof Date ? valeur : new Date(String(valeur));
    return isNaN(d.getTime()) ? null : d;
}

/** Minuit UTC du jour civil, pour comparer au jour près. */
function jourUtc(d: Date): number {
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

/** Vrai si les deux valeurs désignent le même jour, ou sont toutes deux vides. */
export function memeJour(a: unknown, b: unknown): boolean {
    const da = enDate(a);
    const db = enDate(b);
    if (!da && !db) return true;
    if (!da || !db) return false;
    return jourUtc(da) === jourUtc(db);
}

/** Retourne le motif de refus, ou null si la date est acceptable. */
export function verifierDateEntretien(
    dateEntretien: unknown,
    datePremiereInscription?: unknown,
    maintenant: Date = new Date(),
): MotifDateEntretien | null {
    const entretien = enDate(dateEntretien);
    if (!entretien) return 'invalide';

    const jourEntretien = jourUtc(entretien);

    // Tolérance d'un jour sur le futur : entre minuit et 2 h du matin, heure de
    // Paris, le jour UTC est encore la veille et la date du jour serait refusée.
    if (jourEntretien > jourUtc(maintenant) + UN_JOUR_MS) return 'hors_periode';

    const inscription = enDate(datePremiereInscription);
    if (
        inscription &&
        inscription.getUTCFullYear() >= ANNEE_MIN_INSCRIPTION_FIABLE &&
        jourEntretien < jourUtc(inscription)
    ) return 'hors_periode';

    const unAnAvant = new Date(maintenant.getTime());
    unAnAvant.setUTCFullYear(unAnAvant.getUTCFullYear() - 1);
    if (jourEntretien < jourUtc(unAnAvant)) return 'trop_ancienne';

    return null;
}
